import * as React from "react";
import { useState, useMemo } from 'react';
import { 
    AutocompleteArrayInput, 
    DateTimeInput, 
    Loading, 
    ReferenceArrayInput, 
    SimpleForm, 
    required, 
    useLocaleState,
    useTranslate,
    useDataProvider
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { 
    Box, 
    Card, 
    CardContent, 
    Grid, 
    Button, 
    Typography, 
    Divider,
    GlobalStyles,
    CircularProgress
} from '@mui/material';

// Icons coerenti
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';

import { useUserPreferences } from "../hooks/useUserPreferences";
import { getDateInCurrentUsersTimezone } from "../utils/DateUtils";

// --- STILE SCROLLBAR (Identico alla Matrix) ---
const scrollbarStyles = {
    '*::-webkit-scrollbar': { width: '14px', height: '14px' },
    '*::-webkit-scrollbar-track': { backgroundColor: '#f5f5f5', borderLeft: '1px solid #e0e0e0' },
    '*::-webkit-scrollbar-thumb': { backgroundColor: '#b0bec5', borderRadius: '8px', border: '3px solid #f5f5f5' },
    '*::-webkit-scrollbar-thumb:hover': { backgroundColor: '#78909c' },
};

// --- COMPONENTE SELECT ALL (Coerente) ---
const SelectAllEmployeesButton = () => {
    const dataProvider = useDataProvider();
    const { setValue } = useFormContext();
    const translate = useTranslate();
    const [loading, setLoading] = useState(false);

    const handleSelectAll = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('employee', {
                pagination: { page: 1, perPage: 1000 }, 
                sort: { field: 'fullname', order: 'ASC' },
                filter: {}
            });
            const allIds = data.map((record: any) => record.id);
            setValue('employee', allIds, { shouldValidate: true });
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button 
            onClick={handleSelectAll} 
            disabled={loading}
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <PlaylistAddCheckIcon />}
            sx={{ mb: 1, textTransform: 'none' }}
        >
            {translate('ess.calendar.report.select_all_employees', { _: 'Seleziona tutti i dipendenti' })}
        </Button>
    );
};

export default function EmployeeSummaryList() {
    const [pdfSource, setPdfSource] = useState('');
    const [locale] = useLocaleState();
    const { userPreferences, upLoading } = useUserPreferences();
    const translate = useTranslate();

    // --- DATE DI DEFAULT ---
    const { defaultStart, defaultEnd } = useMemo(() => {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0);
        const last = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59);
        return { defaultStart: first, defaultEnd: last };
    }, []);

    if (upLoading || !userPreferences) { return <Loading />; }

    const handleSubmit = (data: any) => {
        const tz = userPreferences.timezone;
        const startIso = getDateInCurrentUsersTimezone(data.start, tz).toISOString();
        const endIso = getDateInCurrentUsersTimezone(data.end, tz).toISOString();
        
        let url = `../api/event/print?locale=${locale}&start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}&timezone=${tz}`;
        
        if (data.employee && data.employee.length > 0) {
            url += `&employees=${data.employee.join(",")}`;
        }
        setPdfSource(url);
    };

    return (
        <Grid container spacing={2} sx={{ p: 2, height: '100%' }}>
            <GlobalStyles styles={scrollbarStyles} />
            
            {/* FILTRI (Colonna Sinistra) */}
            <Grid size={{ xs: 12, md: 3 }}>
                <Card sx={{ 
                    height: "85vh", // Altezza fissa coerente con la tabella
                    display: 'flex', 
                    flexDirection: 'column' // Fondamentale per rendere il contenuto scrollabile e header fisso
                }}>
                    {/* HEADER FISSO (Sticky) */}
                    <Box sx={{ 
                        p: 2, 
                        borderBottom: '1px solid #eee', 
                        bgcolor: '#fafafa',
                        flexShrink: 0 // Impedisce all'header di rimpicciolirsi
                    }}>
                        <Typography variant="h6" color="primary">
                            {translate('ess.calendar.report.parameters', { _: 'Parametri Report' })}
                        </Typography>
                    </Box>
                    
                    {/* CONTENUTO SCROLLABILE */}
                    <CardContent sx={{ 
                        flex: 1, 
                        overflowY: 'auto', // Attiva lo scroll verticale se i campi eccedono l'altezza
                        pt: 2,
                        // Applichiamo gli stili scrollbar personalizzati
                        '&::-webkit-scrollbar': { width: '10px' },
                        '&::-webkit-scrollbar-track': { backgroundColor: '#f5f5f5' },
                        '&::-webkit-scrollbar-thumb': { 
                            backgroundColor: '#b0bec5', 
                            borderRadius: '8px',
                            border: '2px solid #f5f5f5' 
                        }
                    }}>
                        <SimpleForm
                            onSubmit={handleSubmit}
                            toolbar={false}
                            defaultValues={{ start: defaultStart, end: defaultEnd }}
                        >
                            <Box display="flex" flexDirection="column" gap={2} width="100%">
                                <DateTimeInput 
                                    source="start" 
                                    label={translate('ess.calendar.report.start_date', {_: 'Data Inizio'})} 
                                    validate={required()} 
                                    fullWidth 
                                />
                                <DateTimeInput 
                                    source="end" 
                                    label={translate('ess.calendar.report.end_date', {_: 'Data Fine'})} 
                                    validate={required()} 
                                    fullWidth 
                                />
                                
                                <Box>
                                    <SelectAllEmployeesButton />
                                    <ReferenceArrayInput source="employee" reference="employee">
                                        <AutocompleteArrayInput 
                                            label={translate('ess.calendar.report.employee', {_: 'Dipendente'})}
                                            optionText="fullname" 
                                            filterToQuery={searchText => ({ fullname: { ilike: `${searchText}` } })}
                                            fullWidth
                                            helperText={translate('ess.calendar.report.select_employee_helper', {_: 'Seleziona uno o più dipendenti'})}
                                        />
                                    </ReferenceArrayInput>
                                </Box>

                                {/* Altri campi se ne aggiungerai in futuro... */}

                                <Divider sx={{ my: 1 }} />

                                <Button 
                                    type="submit" 
                                    variant="contained" 
                                    size="large"
                                    startIcon={<SettingsSuggestIcon />}
                                    fullWidth
                                >
                                    {translate('ess.calendar.report.preview', {_: 'Genera PDF'})}
                                </Button>
                                
                                <Button 
                                    variant="outlined" 
                                    color="inherit"
                                    startIcon={<RestartAltIcon />}
                                    fullWidth
                                    onClick={() => window.location.reload()}
                                    sx={{ mt: 1 }}
                                >
                                    Reset
                                </Button>
                            </Box>
                        </SimpleForm>
                    </CardContent>
                </Card>
            </Grid>

            {/* PREVIEW PDF (Colonna Destra) */}
            <Grid size={{ xs: 12, md: 9 }}>
                <Card sx={{ height: "85vh", display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #eee', bgcolor: '#fafafa' }}>
                        <Typography variant="h6">
                            {translate('ess.calendar.report.results', { _: 'Anteprima PDF' })}
                        </Typography>
                    </Box>

                    <CardContent sx={{ flex: 1, p: 0, bgcolor: pdfSource ? '#525659' : '#fff' }}>
                        {pdfSource ? (
                            <iframe 
                                src={pdfSource} 
                                width="100%" 
                                height="100%" 
                                style={{ border: 'none' }}
                                title="PDF Summary"
                            />
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height="100%" flexDirection="column" gap={2}>
                                <PictureAsPdfIcon sx={{ fontSize: 80, color: '#ccc' }} />
                                <Typography color="textSecondary">
                                    {translate('ess.calendar.report.select_params', {_: 'Seleziona i parametri e clicca su Genera PDF'})}
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
}